import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  NumberInput,
  Checkbox,
  Divider,
  Stepper,
  SimpleGrid,
  Alert,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCreditCard,
  IconBuildingBank,
  IconCheck,
  IconCash,
  IconDiscount2,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { useInvoices, useMakePayment, useSavedPaymentMethods, useDiscountEligibility } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH Bank Transfer', icon: IconBuildingBank },
  { value: 'credit_card', label: 'Credit Card', icon: IconCreditCard },
  { value: 'check', label: 'Check', icon: IconCash },
];

export default function MakePayment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedInvoiceId = searchParams.get('invoice');
  const payAll = searchParams.get('payAll') === 'true';

  const [step, setStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [amounts, setAmounts] = useState<Record<number, number>>({});
  const [paymentMethod, setPaymentMethod] = useState<string>('ach');
  const [reference] = useState('');

  const { data, isLoading } = useInvoices({ status: 'open' });
  const { data: overdueData } = useInvoices({ status: 'overdue' });
  const { data: partialData } = useInvoices({ status: 'partial' });
  const makePayment = useMakePayment();
  const { data: savedMethods } = useSavedPaymentMethods();
  const firstSelectedId = Array.from(selectedIds)[0] ?? 0;
  const { data: discount } = useDiscountEligibility(firstSelectedId);

  const unpaidInvoices = useMemo(() => {
    const all = [
      ...(data?.items ?? []),
      ...(overdueData?.items ?? []),
      ...(partialData?.items ?? []),
    ];
    const seen = new Set<number>();
    return all.filter((inv) => {
      if (seen.has(inv.id)) return false;
      seen.add(inv.id);
      return parseFloat(inv.balance_due) > 0;
    });
  }, [data, overdueData, partialData]);

  useEffect(() => {
    if (payAll && unpaidInvoices.length > 0) {
      const ids = new Set(unpaidInvoices.map((inv) => inv.id));
      const amts: Record<number, number> = {};
      unpaidInvoices.forEach((inv) => {
        amts[inv.id] = parseFloat(inv.balance_due);
      });
      setSelectedIds(ids);
      setAmounts(amts);
    } else if (preselectedInvoiceId && unpaidInvoices.length > 0) {
      const id = parseInt(preselectedInvoiceId, 10);
      const inv = unpaidInvoices.find((i) => i.id === id);
      if (inv) {
        setSelectedIds(new Set([id]));
        setAmounts({ [id]: parseFloat(inv.balance_due) });
      }
    }
  }, [preselectedInvoiceId, payAll, unpaidInvoices]);

  const toggleInvoice = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setAmounts((a) => {
          const copy = { ...a };
          delete copy[id];
          return copy;
        });
      } else {
        next.add(id);
        const inv = unpaidInvoices.find((i) => i.id === id);
        if (inv) {
          setAmounts((a) => ({ ...a, [id]: parseFloat(inv.balance_due) }));
        }
      }
      return next;
    });
  };

  const totalPayment = Object.entries(amounts)
    .filter(([id]) => selectedIds.has(Number(id)))
    .reduce((sum, [, amt]) => sum + amt, 0);

  const canProceed = step === 0
    ? selectedIds.size > 0 && totalPayment > 0
    : step === 1
    ? !!paymentMethod
    : true;

  const handleSubmit = () => {
    const invoiceAmounts: Record<number, number> = {};
    for (const id of selectedIds) {
      invoiceAmounts[id] = amounts[id] ?? 0;
    }
    makePayment.mutate(
      {
        invoice_ids: Array.from(selectedIds),
        amounts: invoiceAmounts,
        payment_method: paymentMethod,
        reference: reference || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Payment submitted',
            message: `Payment of $${totalPayment.toFixed(2)} has been submitted successfully.`,
            color: 'brand',
          });
          navigate('/billing/payments');
        },
        onError: () => {
          notifications.show({
            title: 'Payment failed',
            message: 'Unable to process payment. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  return (
    <Stack gap="lg">
      <Group>
        <Button
          variant="subtle"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Group>

      <PageHeader title="Make a Payment" description="Select invoices and submit payment" />

      <Stepper active={step} onStepClick={setStep} allowNextStepsSelect={false}>
        <Stepper.Step label="Select Invoices" description="Choose what to pay">
          {isLoading ? (
            <Stack gap="xs" mt="md">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} height={50} radius="md" />
              ))}
            </Stack>
          ) : unpaidInvoices.length === 0 ? (
            <EmptyState
              icon={<IconCheck size={30} />}
              title="All caught up!"
              description="You have no outstanding invoices"
            />
          ) : (
            <Paper mt="md">
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40} />
                    <Table.Th>Invoice #</Table.Th>
                    <Table.Th>Due Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th ta="right">Balance</Table.Th>
                    <Table.Th ta="right" w={160}>Pay Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {unpaidInvoices.map((inv) => {
                    const balance = parseFloat(inv.balance_due);
                    const isSelected = selectedIds.has(inv.id);
                    return (
                      <Table.Tr key={inv.id}>
                        <Table.Td>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleInvoice(inv.id)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{inv.invoice_number}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{dayjs(inv.due_date).format('MMM D, YYYY')}</Text>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge status={inv.status} />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={500} c="red">${balance.toFixed(2)}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          {isSelected && (
                            <NumberInput
                              size="xs"
                              min={0.01}
                              max={balance}
                              step={0.01}
                              decimalScale={2}
                              fixedDecimalScale
                              value={amounts[inv.id] ?? balance}
                              onChange={(v) =>
                                setAmounts((a) => ({ ...a, [inv.id]: Number(v) || 0 }))
                              }
                              styles={{ input: { textAlign: 'right' } }}
                              prefix="$"
                            />
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              <Divider my="sm" />
              <Group justify="flex-end" px="md" pb="sm">
                <Text fw={600}>Total: ${totalPayment.toFixed(2)}</Text>
              </Group>
            </Paper>
          )}
        </Stepper.Step>

        <Stepper.Step label="Payment Method" description="How to pay">
          <Stack gap="md" mt="md">
            {savedMethods && savedMethods.length > 0 && (
              <>
                <Text fw={600} size="sm">Saved Payment Methods</Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  {savedMethods.map((sm) => (
                    <Paper
                      key={sm.id}
                      p="lg"
                      style={{
                        cursor: 'pointer',
                        border: paymentMethod === `saved:${sm.id}`
                          ? '2px solid var(--mantine-color-brand-5)'
                          : '2px solid var(--mantine-color-gray-2)',
                        transition: 'border-color 150ms ease',
                      }}
                      onClick={() => setPaymentMethod(`saved:${sm.id}`)}
                    >
                      <Stack align="center" gap="xs">
                        {sm.type === 'credit_card' ? (
                          <IconCreditCard size={32} color={paymentMethod === `saved:${sm.id}` ? 'var(--mantine-color-brand-6)' : undefined} />
                        ) : (
                          <IconBuildingBank size={32} color={paymentMethod === `saved:${sm.id}` ? 'var(--mantine-color-brand-6)' : undefined} />
                        )}
                        <Text fw={500} size="sm">{sm.label}</Text>
                        {sm.is_default && (
                          <Text size="xs" c="dimmed">Default</Text>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
                <Divider label="Or use a new method" labelPosition="center" />
              </>
            )}
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {PAYMENT_METHODS.map((pm) => (
                <Paper
                  key={pm.value}
                  p="lg"
                  style={{
                    cursor: 'pointer',
                    border: paymentMethod === pm.value
                      ? '2px solid var(--mantine-color-brand-5)'
                      : '2px solid var(--mantine-color-gray-2)',
                    transition: 'border-color 150ms ease',
                  }}
                  onClick={() => setPaymentMethod(pm.value)}
                >
                  <Stack align="center" gap="xs">
                    <pm.icon size={32} color={paymentMethod === pm.value ? 'var(--mantine-color-brand-6)' : undefined} />
                    <Text fw={500} size="sm">{pm.label}</Text>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        </Stepper.Step>

        <Stepper.Step label="Confirm" description="Review and submit">
          <Stack gap="md" mt="md">
            <Paper>
              <Text fw={600} mb="sm">Payment Summary</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Invoice</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {Array.from(selectedIds).map((id) => {
                    const inv = unpaidInvoices.find((i) => i.id === id);
                    return (
                      <Table.Tr key={id}>
                        <Table.Td><Text size="sm">{inv?.invoice_number ?? `#${id}`}</Text></Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={500}>${(amounts[id] ?? 0).toFixed(2)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
                <Table.Tfoot>
                  <Table.Tr>
                    <Table.Td><Text fw={700}>Total</Text></Table.Td>
                    <Table.Td ta="right"><Text fw={700}>${totalPayment.toFixed(2)}</Text></Table.Td>
                  </Table.Tr>
                </Table.Tfoot>
              </Table>
            </Paper>

            <Paper>
              <Group gap="lg">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Payment Method</Text>
                  <Text fw={500} tt="capitalize" mt={2}>
                    {PAYMENT_METHODS.find((m) => m.value === paymentMethod)?.label ?? paymentMethod}
                  </Text>
                </div>
              </Group>
            </Paper>

            {discount?.eligible && (
              <Alert icon={<IconDiscount2 size={20} />} color="green" variant="light">
                Early payment discount available! Pay by {dayjs(discount.deadline).format('MMM D, YYYY')} to save ${discount.discount_amount}.
              </Alert>
            )}

            <Alert color="blue" variant="light">
              By clicking "Submit Payment" you authorize this payment. Processing times vary by method.
            </Alert>
          </Stack>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button
          variant="subtle"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          Back
        </Button>
        {step < 2 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canProceed}>
            Continue
          </Button>
        ) : (
          <Button onClick={handleSubmit} loading={makePayment.isPending} disabled={!canProceed}>
            Submit Payment
          </Button>
        )}
      </Group>
    </Stack>
  );
}
